import * as React from 'react';

import DocumentCreationButton from 'Components/DocumentCreationButton';
import DocumentHeader from 'Components/DocumentHeader';
import DocumentWrapper from 'Components/DocumentWrapper';
import FormEditingHandler from 'Containers/FormEditingHandler';
import DoctorsOrdersForm from './Form';
import DoctorsOrdersHistory from './HistoryRowData';
import { useIntl } from 'react-intl';
import AdditionalDocumentsForm from './Form/AdditionalDocuments';
import { sortPartialDate } from 'neuro-utils';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import { filterByDocumentType, filterDocumentsByType, formDataByType } from 'Utility/documentHandling';

const EditButton = ({ startEdit }: IEditButton): React.JSX.Element => (
  <div>
    <DocumentCreationButton name="doctorsOrders" text="general.new" onClick={startEdit({})} />
  </div>
);

interface IEditButton {
  startEdit: StartEdit;
}

export const DoctorsOrdersContext = React.createContext<IDoctorsOrderContext>({
  fm: (m: string) => m,
  isEditing: false,
  view: undefined,
  formData: {
    document: {},
    onChange: () => {
      return;
    },
  },
  startEdit: (d: { _id?: string }) => (e: React.MouseEvent<Element, MouseEvent> | undefined) => {
    !!d;
    !!e;
    return;
  },
  documents: [],
  additionalDocuments: [],
  ninmtTreatmentPeriods: [],
});

export interface IDoctorsOrderContext {
  fm: (m: string, values?: { [key: string]: string }) => string;
  isEditing: boolean;
  view?: IView;
  formData: IFormData<IDocumentWithTypeV2<'doctorsOrders'>>;
  documents: IDocumentWithTypeV2<'doctorsOrders'>[];
  additionalDocuments?: IDocumentWithTypeV2<'doctorsOrdersAdditional'>[];
  ninmtTreatmentPeriods?: IDocumentWithTypeV2<'ninmtTreatmentPeriod'>[];
  startEdit: StartEdit;
}

const DoctorsOrdersContent = ({
  editing,
  startEdit,
  formData,
  view,
  fm,
  documents,
  activeView,
  setActiveView,
}: IDoctorsOrdersContentProps): React.JSX.Element => {
  const isEditing = !!editing && !view?.viewing;
  const showForm = !!editing || !!view?.viewing;

  const parsedDocs = structuredClone(documents);
  const doctorsOrdersAdditionalDocs = filterByDocumentType(parsedDocs, 'doctorsOrdersAdditional');
  const ninmtTreatmentPeriodDocs = filterByDocumentType(parsedDocs, 'ninmtTreatmentPeriod');

  const doctorsOrdersDocs = filterByDocumentType(parsedDocs, 'doctorsOrders')
    .toSorted((n1, n2) => n2._cdate - n1._cdate)
    .toSorted((n1, n2) => sortPartialDate(n2.date, n1.date));

  const document = React.useMemo(
    () => [...doctorsOrdersDocs, ...doctorsOrdersAdditionalDocs].find(d => [editing, view?.viewing].includes(d._id)),
    [doctorsOrdersDocs, doctorsOrdersAdditionalDocs, editing, view],
  );

  const editingDocumentType = documents.find(d => d._id === editing)?._type;

  const doctorsOrdersContext = React.useMemo(
    () => ({
      fm,
      isEditing,
      view,
      // Only the doctorsOrders subforms read context.formData, and they render solely under the doctorsOrders branch below; an additional document never reaches them
      formData: formData as IFormData<IDocumentWithTypeV2<'doctorsOrders'>>,
      documents: doctorsOrdersDocs,
      additionalDocuments: doctorsOrdersAdditionalDocs,
      ninmtTreatmentPeriods: ninmtTreatmentPeriodDocs,
      startEdit,
    }),
    [
      fm,
      isEditing,
      view,
      formData,
      doctorsOrdersDocs,
      doctorsOrdersAdditionalDocs,
      ninmtTreatmentPeriodDocs,
      startEdit,
    ],
  );

  return (
    <React.Fragment>
      <DoctorsOrdersContext.Provider value={doctorsOrdersContext}>
        <DocumentWrapper>
          <DocumentHeader
            name="doctorsOrders"
            headerId={
              document && document._type === 'doctorsOrdersAdditional'
                ? 'doctorsOrders.additionalDocuments.header'
                : 'doctorsOrders.title'
            }
            editing={editing || view?.viewing}
            editButtons={<EditButton startEdit={startEdit} />}
            infoText={
              !isEditing || editingDocumentType === 'doctorsOrders' ? (
                !isEditing ? (
                  'doctorsOrders.infoText'
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div style={{ fontWeight: '600' }}>
                      {activeView === 'subjectOfOrder'
                        ? fm('doctorsOrders.infoTextSubjectOfOrder')
                        : fm('doctorsOrders.infoTextForm')}
                    </div>
                    {activeView === 'form' && (
                      <ActionButtonRounded
                        width={18}
                        height={4}
                        fontSize={16}
                        text="doctorsOrders.subjectOfOrder.goBack"
                        onClick={() => setActiveView('subjectOfOrder')}
                        colorScheme="cancel"
                      />
                    )}
                  </div>
                )
              ) : undefined
            }
            headerWidth={isEditing && activeView === 'form' ? 12 : 7}
          />
          {showForm && document ? (
            document._type === 'doctorsOrdersAdditional' ? (
              <AdditionalDocumentsForm formData={formDataByType(formData, document)} />
            ) : Boolean(document._type) ? (
              <DoctorsOrdersForm
                formData={formDataByType(formData, document)}
                activeView={activeView}
                setActiveView={setActiveView}
              />
            ) : null
          ) : null}
          {!showForm ? <DoctorsOrdersHistory startEdit={startEdit} /> : null}
        </DocumentWrapper>
      </DoctorsOrdersContext.Provider>
    </React.Fragment>
  );
};

const DoctorsOrders = ({ documents }: IDoctorsOrdersProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string, values?: { [key: string]: string }) => formatMessage({ id: id }, values);

  const [activeView, setActiveView] = React.useState<'subjectOfOrder' | 'form'>('subjectOfOrder');

  return (
    <FormEditingHandler
      name="doctorsOrders"
      documents={filterDocumentsByType(documents, ['doctorsOrders', 'doctorsOrdersAdditional'])}
    >
      {(editing, startEdit, formData, view): React.JSX.Element => (
        <DoctorsOrdersContent
          editing={editing}
          startEdit={startEdit}
          formData={formData}
          view={view}
          fm={fm}
          documents={documents}
          activeView={activeView}
          setActiveView={setActiveView}
        />
      )}
    </FormEditingHandler>
  );
};

interface IDoctorsOrdersContentProps {
  editing: string;
  startEdit: StartEdit;
  formData: IFormData<IDocumentWithTypeV2<'doctorsOrders' | 'doctorsOrdersAdditional' | 'ninmtTreatmentPeriod'>>;
  view?: IView;
  fm: (m: string, values?: { [key: string]: string }) => string;
  documents: IDocumentWithTypeV2<'doctorsOrders' | 'doctorsOrdersAdditional' | 'ninmtTreatmentPeriod'>[];
  activeView: 'subjectOfOrder' | 'form';
  setActiveView: React.Dispatch<React.SetStateAction<'subjectOfOrder' | 'form'>>;
}

interface IDoctorsOrdersProps {
  documents: IDocumentWithTypeV2<'doctorsOrders' | 'doctorsOrdersAdditional' | 'ninmtTreatmentPeriod'>[];
}

export default DoctorsOrders;
